import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import {
  ExternalDropZone,
  Upload,
  UploadFileInfo,
  UploadListItemProps,
  UploadOnRemoveEvent,
  UploadOnStatusChangeEvent,
} from '@progress/kendo-react-upload';
import axios from 'axios';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScanManagerErrorColumns } from 'src/assets/static/scan_manager/ScanManagerErrorTable';
import Button from 'src/components/design-system/Button';
import PopUp from 'src/components/design-system/PopUp';
import GridWrapper from 'src/components/GridWrapper';
import Modal from 'src/components/Modal';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import UploadFileListItem from 'src/components/UploadFileListItem';
import { EMAIL_PREFIX } from 'src/config';
import { UploadPdfOnly } from 'src/constants/permissionsList';
import useBoolean from 'src/hooks/useBoolean';
import useCompany from 'src/hooks/useCompany';
import useConfirm from 'src/hooks/useConfirm';
import useInvoiceScanManager from 'src/hooks/useInvoiceScanManager';
import usePermission from 'src/hooks/usePermissions';
import useWindowSize from 'src/hooks/useWindowSize';
import useInvoiceS3UploadApi from 'src/services/useInvoiceS3UploadApi';
import useRunOcrScanApi, { OcrAttachmentEntry } from 'src/services/useRunOcrScanApi';
import useS3InvoiceAttach, { S3UploadInvoiceAttachRequest } from 'src/services/useS3InvoiceAttach';
import { SetState } from 'src/types/generic';
import {
  allowedOcrFileExtensions,
  createInitialPdfFileMeta,
  filterPreviewableUploadFiles,
  hasPageRangeDigits,
  inspectPdf,
  isPdfFile,
  PdfFileMeta,
  PreRequiredCheck,
  ProcessedFilesState,
  S3FileValidations,
  unProcessedFiles,
  validatePageRange,
} from 'src/utils/fileValidation';
import UploadFilePreviewPanel from './UploadFilePreviewPanel';

export interface S3AttachmentResponse {
  processedFiles: string[];
  unProcessed: string[];
}

export interface S3UploadRes {
  fileName: string | null;
  signedUrl: string;
  fileNameWithAttachId: string;
  attachId: number;
}

const uploadRef = React.createRef<Upload>();
const hint = (
  <span>
    <i>Drop files here to upload</i>
  </span>
);
type UploadFilesModalProps = {
  closeModal: VoidFunction;
  open: boolean;
  refreshParent: VoidFunction;
  setRefreshCompany?: SetState<boolean>;
  id?: number;
};
const InvoiceUploadFilesModal = ({
  closeModal,
  open,
  refreshParent,
  setRefreshCompany,
  id,
}: UploadFilesModalProps) => {
  const { t } = useTranslation();
  const { company: selectedCompany } = useCompany();
  const { height, width } = useWindowSize();
  const { ocrScanRun } = useRunOcrScanApi();
  const { isOcrCompany, setSelectedView, getOcrRecordCount } = useInvoiceScanManager();
  const { S3Files: executeInvoiceUpload } = useInvoiceS3UploadApi();
  const { S3InvoiceAttach } = useS3InvoiceAttach(parseInt(selectedCompany.companyId), id);
  const { confirm } = useConfirm();
  const isPDFOnly = usePermission({ permissions: UploadPdfOnly });
  const [processedFileState, setProcessedFileState] = useState<ProcessedFilesState>({
    processedFiles: [],
    preRequiredCheck: { processed: [], unProcessed: [] },
  });
  const [pdfMeta, setPdfMeta] = useState<Record<string, PdfFileMeta>>({});
  const [selectedFileUid, setSelectedFileUid] = useState<string | null>(null);
  const [uploadListFiles, setUploadListFiles] = useState<UploadFileInfo[]>([]);
  const [previewableFiles, setPreviewableFiles] = useState<UploadFileInfo[]>([]);
  const modalContentRef = useRef<HTMLDivElement>(null);
  const selectedCompanyName = useMemo(
    () => selectedCompany.companyName.split('(')[0],
    [selectedCompany.companyName]
  );
  const {
    value: isUploadLoading,
    setTrue: startUploadLoading,
    setFalse: endUploadLoading,
  } = useBoolean();
  const {
    value: isUploadAndScanLoading,
    setTrue: startUploadAndScanLoading,
    setFalse: endUploadAndScanLoading,
  } = useBoolean();
  const isUploadButtonDisable =
    processedFileState.preRequiredCheck.processed.length === 0 &&
    processedFileState.preRequiredCheck.unProcessed.length === 0;
  const isUnsupportedFileType =
    processedFileState.preRequiredCheck.processed.some(
      (file) => !allowedOcrFileExtensions.some((ext) => file.extension?.toLowerCase().includes(ext))
    ) || processedFileState.preRequiredCheck.unProcessed.length > 0;

  const isAllFilePdf =
    processedFileState.processedFiles.every((file) => file.extension?.toLowerCase() === '.pdf') &&
    processedFileState.preRequiredCheck.unProcessed.length === 0;

  const onSelectFilesClick = () => {
    if (uploadRef) uploadRef?.current?.actionElement?.click();
  };

  const postAttachments = useCallback(
    async (
      attachMethod: Function,
      requestData: S3UploadRes[],
      preRequiredCheck: PreRequiredCheck
    ): Promise<{
      preRequiredCheck: PreRequiredCheck;
    }> => {
      const { processed, unProcessed } = preRequiredCheck;

      if (requestData.length === 0) {
        console.warn('Processed files array is empty. Aborting file upload.');
        return { preRequiredCheck };
      }
      const modifiedReqData = requestData.map((file) => {
        const fileNameParts = file.fileNameWithAttachId.split('.');
        const extension = fileNameParts.pop();
        return { ...file, fileName: `${file.fileName}.${extension}` };
      });
      const fileNames = modifiedReqData.map((file: any) => file.fileName);

      try {
        if (processed.length) {
          await attachMethod(modifiedReqData, id);
        }

        return {
          preRequiredCheck,
        };
      } catch (error) {
        const errors: unProcessedFiles[] = fileNames.map((fileName) => ({
          fileName,
          errorMessage: 'Failed to upload file',
        }));
        preRequiredCheck.processed = [];
        unProcessed.push(...errors);
        return {
          preRequiredCheck,
        };
      }
    },
    [id]
  );

  const putPresignedUrls = useCallback(
    async (
      s3response: S3UploadRes[],
      processedFiles: UploadFileInfo[],
      preRequiredCheck: PreRequiredCheck
    ): Promise<{
      preRequiredCheck: PreRequiredCheck;
      awsResponse: string[];
    }> => {
      if (processedFiles.length === 0) {
        console.warn('Processed files array is empty. Aborting file upload.');
        return { preRequiredCheck, awsResponse: [] };
      }
      const modifiedReqData = s3response.map((file) => {
        const fileNameParts = file.fileNameWithAttachId.split('.');
        const extension = fileNameParts.pop();
        return { ...file, fileName: `${file.fileName}.${extension}` };
      });

      try {
        const awsResponse = await Promise.all(
          modifiedReqData.map(async (presignedUrls, index) => {
            // @ts-ignore
            const file = processedFiles[index].getRawFile();
            try {
              const response = await axios.put(presignedUrls.signedUrl, await file.arrayBuffer(), {
                headers: {
                  'Content-Type': file.type,
                },
              });
              const status = response.status === 200 ? 'success' : 'Fail';
              return status;
            } catch (error) {
              return 'Fail';
            }
          })
        );
        if (awsResponse.includes('Fail')) {
          preRequiredCheck.processed = [];
          const errors: unProcessedFiles[] = processedFiles.map((fileName) => ({
            fileName: fileName.name,
            errorMessage: 'Failed to upload file',
          }));
          preRequiredCheck.processed = [];
          preRequiredCheck.unProcessed.push(...errors);
          return { preRequiredCheck, awsResponse: ['Fail'] };
        }
        return { preRequiredCheck, awsResponse };
      } catch (error) {
        preRequiredCheck.processed = [];
        const errors: unProcessedFiles[] = processedFiles.map((fileName) => ({
          fileName: fileName.name,
          errorMessage: 'Failed to upload file',
        }));
        preRequiredCheck.processed = [];
        preRequiredCheck.unProcessed.push(...errors);
        return { preRequiredCheck, awsResponse: ['Fail'] };
      }
    },
    []
  );

  const getSignedUrls = useCallback(
    async (
      uploadMethod: Function,
      processedFiles: UploadFileInfo[],
      preRequiredCheck: PreRequiredCheck
    ): Promise<{
      preRequiredCheck: PreRequiredCheck;
      s3response: S3UploadInvoiceAttachRequest[];
    }> => {
      if (processedFiles.length === 0) {
        console.warn('Processed files array is empty. Aborting file upload.');
        return { preRequiredCheck, s3response: [] };
      }
      const fileNames = processedFiles.map((file: any) => file.name);
      try {
        const s3response: S3UploadInvoiceAttachRequest[] = await uploadMethod(fileNames);
        return { preRequiredCheck, s3response };
      } catch (error) {
        const errors: unProcessedFiles[] = fileNames.map((fileName) => ({
          fileName,
          errorMessage: 'Failed to upload file',
        }));
        preRequiredCheck.processed = [];
        preRequiredCheck.unProcessed.push(...errors);
        return { preRequiredCheck, s3response: [] };
      }
    },
    []
  );

  const postScanAttachments = useCallback(
    async (
      ocrScanMethod: Function,
      requestData: S3UploadRes[],
      processedFiles: UploadFileInfo[],
      preRequiredCheck: PreRequiredCheck,
      pageMetaByUid: Record<string, PdfFileMeta>
    ): Promise<{
      preRequiredCheck: PreRequiredCheck;
    }> => {
      const { processed, unProcessed } = preRequiredCheck;

      if (requestData.length === 0) {
        console.warn('Processed files array is empty. Aborting file upload.');
        return { preRequiredCheck };
      }
      try {
        if (processed.length) {
          const attachments: OcrAttachmentEntry[] = requestData.map((file, index) => {
            const uid = processedFiles[index]?.uid;
            const confirmedRange = uid ? pageMetaByUid[uid]?.confirmedRange : null;
            return {
              attachmentId: file.attachId,
              ...(confirmedRange ? { pageNumberRange: confirmedRange } : {}),
            };
          });
          await ocrScanMethod({
            companyId: Number(selectedCompany.companyId),
            attachments,
          });
        }
        return {
          preRequiredCheck,
        };
      } catch (error) {
        const errors: unProcessedFiles[] = requestData.map((file) => ({
          fileName: file.fileName as string,
          errorMessage: 'Failed to scan file',
        }));
        preRequiredCheck.processed = [];
        unProcessed.push(...errors);
        return {
          preRequiredCheck,
        };
      }
    },
    [selectedCompany?.companyId]
  );

  const loadPdfMetaForFile = useCallback(async (file: UploadFileInfo) => {
    if (!(await isPdfFile(file))) {
      return;
    }

    const rawFile = file.getRawFile?.() as File | undefined;
    if (!rawFile) {
      setPdfMeta((prev) => ({
        ...prev,
        [file.uid]: {
          ...createInitialPdfFileMeta(),
          isLoading: false,
          error: 'corrupt',
        },
      }));
      return;
    }

    setPdfMeta((prev) => ({
      ...prev,
      [file.uid]: {
        ...createInitialPdfFileMeta(),
        isLoading: true,
      },
    }));

    try {
      const { pageCount } = await inspectPdf(rawFile);
      setPdfMeta((prev) => ({
        ...prev,
        [file.uid]: {
          ...createInitialPdfFileMeta(),
          isLoading: false,
          pageCount,
          isPanelOpen: pageCount > 1,
          ...(pageCount === 1 ? { confirmedRange: '1', draftRange: '1' } : {}),
        },
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'corrupt';
      const inspectError = message === 'password-protected' ? 'password-protected' : 'corrupt';
      setPdfMeta((prev) => ({
        ...prev,
        [file.uid]: {
          ...createInitialPdfFileMeta(),
          isLoading: false,
          error: inspectError,
        },
      }));
    }
  }, []);

  const onAdd = useCallback(
    async (e: UploadOnStatusChangeEvent) => {
      const validateFiles = await S3FileValidations(e?.newState, isPDFOnly);
      setProcessedFileState(validateFiles);
      setUploadListFiles(e?.newState ?? []);
    },
    [isPDFOnly]
  );

  const handleRemoveFile = useCallback((event: UploadOnRemoveEvent) => {
    const deletedFile = event.affectedFiles[0];
    setUploadListFiles((prev) => prev.filter((file) => file.uid !== deletedFile.uid));
    setProcessedFileState((prev) => ({
      processedFiles: prev.processedFiles.filter((file) => file.uid !== deletedFile.uid),
      preRequiredCheck: {
        processed: prev.preRequiredCheck.processed.filter((file) => file.uid !== deletedFile.uid),
        unProcessed: prev.preRequiredCheck.unProcessed.filter(
          (file) => file.file?.uid !== deletedFile.uid
        ),
      },
    }));
    setPdfMeta((prev) => {
      const next = { ...prev };
      delete next[deletedFile.uid];
      return next;
    });
  }, []);

  const handleConfirmRange = useCallback((uid: string, draftRange: string) => {
    setPdfMeta((prev) => {
      const current = prev[uid];
      if (!current?.pageCount) {
        return prev;
      }
      const validationError = validatePageRange(draftRange, current.pageCount);
      if (validationError) {
        return {
          ...prev,
          [uid]: {
            ...current,
            draftRange,
            validationError,
          },
        };
      }
      return {
        ...prev,
        [uid]: {
          ...current,
          draftRange: draftRange.trim(),
          confirmedRange: draftRange.trim(),
          validationError: null,
          isPanelOpen: false,
        },
      };
    });
  }, []);

  const handleDraftRangeChange = useCallback((uid: string, draftRange: string) => {
    setPdfMeta((prev) => {
      const current = prev[uid];
      if (!current?.pageCount) {
        return prev;
      }
      const trimmed = draftRange.trim();
      return {
        ...prev,
        [uid]: {
          ...current,
          draftRange,
          // Empty draft is not treated as invalid while typing; confirm/blur still validates.
          validationError: trimmed ? validatePageRange(draftRange, current.pageCount) : null,
        },
      };
    });
  }, []);

  const handleEditRange = useCallback((uid: string) => {
    setPdfMeta((prev) => {
      const current = prev[uid];
      if (!current || current.pageCount === 1) {
        return prev;
      }
      return {
        ...prev,
        [uid]: {
          ...current,
          isPanelOpen: true,
          draftRange: current.confirmedRange ?? '',
          validationError: null,
        },
      };
    });
  }, []);

  const handleTogglePanel = useCallback((uid: string, open: boolean) => {
    setPdfMeta((prev) => {
      const current = prev[uid];
      if (!current || (open && current.pageCount === 1)) {
        return prev;
      }
      return {
        ...prev,
        [uid]: {
          ...current,
          isPanelOpen: open,
          draftRange: open
            ? current.draftRange || current.confirmedRange || ''
            : current.confirmedRange ?? '',
          validationError: open ? current.validationError : null,
        },
      };
    });
  }, []);

  const handleClearRange = useCallback((uid: string) => {
    setPdfMeta((prev) => {
      const current = prev[uid];
      if (!current) {
        return prev;
      }
      return {
        ...prev,
        [uid]: {
          ...current,
          draftRange: '',
          confirmedRange: null,
          validationError: null,
          isPanelOpen: false,
        },
      };
    });
  }, []);

  const resolveUploadFile = useCallback(
    (uid: string, fileFromList?: UploadFileInfo): UploadFileInfo | undefined => {
      if (fileFromList) {
        return fileFromList;
      }
      return (
        processedFileState.processedFiles.find((f) => f.uid === uid) ??
        processedFileState.preRequiredCheck.processed.find((f) => f.uid === uid) ??
        processedFileState.preRequiredCheck.unProcessed.find((entry) => entry.file?.uid === uid)
          ?.file
      );
    },
    [processedFileState]
  );

  const handleScanByPageClick = useCallback(
    async (uid: string, fileFromList?: UploadFileInfo) => {
      const existing = pdfMeta[uid];
      if (existing?.pageCount != null && !existing.error && !existing.confirmedRange) {
        if (existing.pageCount === 1) {
          setPdfMeta((prev) => ({
            ...prev,
            [uid]: { ...prev[uid], confirmedRange: '1', draftRange: '1' },
          }));
          return;
        }
        if (existing.pageCount > 1) {
          setPdfMeta((prev) => ({
            ...prev,
            [uid]: { ...prev[uid], isPanelOpen: true },
          }));
        }
        return;
      }

      const file = resolveUploadFile(uid, fileFromList);
      if (!file) {
        setPdfMeta((prev) => ({
          ...prev,
          [uid]: {
            ...createInitialPdfFileMeta(),
            isLoading: false,
            error: 'corrupt',
          },
        }));
        return;
      }

      await loadPdfMetaForFile(file);
    },
    [pdfMeta, resolveUploadFile, loadPdfMetaForFile]
  );

  useEffect(() => {
    let cancelled = false;

    void filterPreviewableUploadFiles(processedFileState.processedFiles).then((files) => {
      if (cancelled) {
        return;
      }
      setPreviewableFiles(files);
      setSelectedFileUid((currentSelected) => {
        if (!currentSelected) {
          return null;
        }
        if (uploadListFiles.some((file) => file.uid === currentSelected)) {
          return currentSelected;
        }
        return uploadListFiles[0]?.uid ?? null;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [processedFileState.processedFiles, uploadListFiles]);

  const selectedListFile = useMemo(() => {
    if (!selectedFileUid) {
      return null;
    }
    return (
      processedFileState.processedFiles.find((file) => file.uid === selectedFileUid) ??
      processedFileState.preRequiredCheck.unProcessed.find(
        (entry) => entry.file?.uid === selectedFileUid
      )?.file ??
      null
    );
  }, [processedFileState, selectedFileUid]);

  const selectedPreviewIndex = useMemo(() => {
    if (!selectedFileUid) {
      return -1;
    }
    return previewableFiles.findIndex((file) => file.uid === selectedFileUid);
  }, [previewableFiles, selectedFileUid]);

  const selectedListIndex = useMemo(() => {
    if (!selectedFileUid) {
      return -1;
    }
    return uploadListFiles.findIndex((file) => file.uid === selectedFileUid);
  }, [uploadListFiles, selectedFileUid]);

  const handleSelectFile = useCallback((uid: string) => {
    setSelectedFileUid(uid);
  }, []);

  const handleSelectPreviewIndex = useCallback(
    (index: number) => {
      const file = previewableFiles[index];
      if (file) {
        setSelectedFileUid(file.uid);
      }
    },
    [previewableFiles]
  );

  // Kendo's Upload uses `listItemUI` as a JSX element type, so a new function
  // identity here forces it to unmount/remount every file row (losing focus on
  // the page-range input mid-edit, which triggers its blur-driven auto-clear).
  // Keep this callback's identity permanently stable and read live state from
  // a ref instead, so Kendo re-renders the rows in place rather than remounting them.
  const listItemStateRef = useRef({
    isOcrCompany,
    pdfMeta,
    handleConfirmRange,
    handleDraftRangeChange,
    handleClearRange,
    handleEditRange,
    handleScanByPageClick,
    handleTogglePanel,
    selectedFileUid,
    handleSelectFile,
  });
  listItemStateRef.current = {
    isOcrCompany,
    pdfMeta,
    handleConfirmRange,
    handleDraftRangeChange,
    handleClearRange,
    handleEditRange,
    handleScanByPageClick,
    handleTogglePanel,
    selectedFileUid,
    handleSelectFile,
  };

  const renderUploadFileListItem = useCallback((listProps: UploadListItemProps) => {
    const {
      isOcrCompany,
      pdfMeta,
      handleConfirmRange,
      handleDraftRangeChange,
      handleClearRange,
      handleEditRange,
      handleScanByPageClick,
      handleTogglePanel,
      selectedFileUid,
      handleSelectFile,
    } = listItemStateRef.current;
    return (
      <UploadFileListItem
        {...listProps}
        showScanByPage={isOcrCompany}
        pdfMeta={pdfMeta}
        onConfirmRange={handleConfirmRange}
        onDraftRangeChange={handleDraftRangeChange}
        onClearRange={handleClearRange}
        onEditRange={handleEditRange}
        onScanByPageClick={handleScanByPageClick}
        onTogglePanel={handleTogglePanel}
        selectedUid={selectedFileUid}
        onSelectFile={handleSelectFile}
      />
    );
  }, []);

  const handleUploadAndScan = useCallback(
    async (runOcr: boolean) => {
      runOcr ? startUploadAndScanLoading() : startUploadLoading();
      const { processedFiles, preRequiredCheck } = processedFileState;
      // s3 file > s3 response, getting s3 url
      const { preRequiredCheck: getSignedUrlsPreReqCheck, s3response } = await getSignedUrls(
        executeInvoiceUpload,
        processedFiles,
        preRequiredCheck
      );
      //  s3 response > success or fail , uploading to s3 with file and url
      const { preRequiredCheck: putPresignedUrlsPreReqCheck } = await putPresignedUrls(
        s3response,
        processedFiles,
        getSignedUrlsPreReqCheck
      );

      // s3 response >  preRequiredCheck
      const { preRequiredCheck: postSignedUrlsPreReqCheck } = await postAttachments(
        S3InvoiceAttach,
        s3response,
        putPresignedUrlsPreReqCheck
      );
      let preRequiredCheckResult = postSignedUrlsPreReqCheck;
      if (runOcr) {
        const { preRequiredCheck: postScanPreReqCheck } = await postScanAttachments(
          ocrScanRun,
          s3response,
          processedFiles,
          postSignedUrlsPreReqCheck,
          pdfMeta
        );
        preRequiredCheckResult = postScanPreReqCheck;
      }
      const { processed, unProcessed } = preRequiredCheckResult;
      closeModal();
      await confirm({
        title: 'File Upload',
        width: width > 700 ? '700px' : 'auto',
        height: processed.length > 0 ? 'auto' : '380px',
        content: (
          <div className="font-bold ">
            <div className="font-bold ">
              {processed.length > 0 && (
                <>
                  {processed.length > 0 ? (
                    <>
                      <span className="font-extrabold text-lg m-2 text-green-600">
                        {processed.length}
                      </span>
                      Record(s) uploaded successfully
                    </>
                  ) : (
                    ''
                  )}
                  {unProcessed?.length > 0 ? ' and' : ''}
                </>
              )}
              {unProcessed?.length > 0 && (
                <>
                  <span className="font-extrabold text-lg m-2 text-red-600">
                    {unProcessed?.length}
                  </span>
                  Record(s) have failed
                  <GridWrapper
                    className="m-2 no_Veritcal_ScrollBar"
                    columns={ScanManagerErrorColumns}
                    dataSource={unProcessed}
                    DataItemKey={'fileName'}
                    pageable={false}
                    tableTextId="Errors"
                    disableAutoResize
                  />
                </>
              )}
              {runOcr && (
                <p className="m-2 text-sm font-semibold">
                  Note: Any issues when creating the document will be flagged as "Needs Review" or
                  "Failed" in the OCR Queue tab.
                </p>
              )}
            </div>
          </div>
        ),
        cancelButtonText: 'Close',
        cancelColor: 'info',
        hasNoConfirmButton: true,
      });
      refreshParent();
      setRefreshCompany?.((prev) => !prev);
      runOcr && getOcrRecordCount();
      if (runOcr) {
        endUploadAndScanLoading();
        setSelectedView(processed.length > 0 ? 1 : 0);
      } else {
        endUploadLoading();
        setSelectedView(0);
      }
    },
    [
      startUploadAndScanLoading,
      startUploadLoading,
      processedFileState,
      getSignedUrls,
      executeInvoiceUpload,
      putPresignedUrls,
      postAttachments,
      S3InvoiceAttach,
      closeModal,
      confirm,
      refreshParent,
      setRefreshCompany,
      endUploadAndScanLoading,
      endUploadLoading,
      postScanAttachments,
      ocrScanRun,
      pdfMeta,
      setSelectedView,
      getOcrRecordCount,
      width,
    ]
  );

  const userEmail = `${EMAIL_PREFIX}-${selectedCompany.companyId}@ibuyefficient.com`;

  const handleMailClick = useCallback(() => {
    const mailSubject = `Invoice Scans for ${selectedCompanyName}`;
    const mailURl = `mailto:${userEmail}?subject=${mailSubject}`;
    window.location.href = mailURl;
  }, [userEmail, selectedCompanyName]);

  const isWideLayout = width >= 1024;
  const showDesktopDropZone = isWideLayout;

  const modalWidth = useMemo(() => {
    const availableWidth = Math.max(width - 24, 280);
    return `${Math.min(1320, availableWidth)}px`;
  }, [width]);

  const modalHeight = useMemo(() => {
    const availableHeight = Math.max(height - 24, 420);
    const maxHeight = isOcrCompany ? 760 : 700;
    return `${Math.min(maxHeight, availableHeight)}px`;
  }, [height, isOcrCompany]);

  useEffect(() => {
    const spanElement = document.querySelector(
      '#uploadInvoiceModal span[aria-hidden="true"]'
    ) as HTMLSpanElement | null;
    if (!spanElement) {
      return;
    }
    if (width <= 1024) {
      spanElement.innerText = 'Select Files or Take Picture';
    } else {
      spanElement.innerText = 'Select files...';
    }
  }, [width]);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      setPdfMeta({});
      setSelectedFileUid(null);
      setUploadListFiles([]);
      setPreviewableFiles([]);
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      const { target } = event;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement
      ) {
        return;
      }

      if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') {
        return;
      }

      if (uploadListFiles.length === 0) {
        return;
      }

      event.preventDefault();

      const currentIndex =
        selectedListIndex >= 0
          ? selectedListIndex
          : event.key === 'ArrowDown'
          ? -1
          : uploadListFiles.length;

      const delta = event.key === 'ArrowDown' ? 1 : -1;
      const nextIndex = Math.min(uploadListFiles.length - 1, Math.max(0, currentIndex + delta));
      setSelectedFileUid(uploadListFiles[nextIndex].uid);
    };

    const node = modalContentRef.current;
    node?.addEventListener('keydown', handleKeyDown);
    return () => node?.removeEventListener('keydown', handleKeyDown);
  }, [open, uploadListFiles, selectedListIndex]);

  const hasPdfInspectError = useMemo(
    () =>
      Object.values(pdfMeta).some(
        (meta) => meta.error === 'password-protected' || meta.error === 'corrupt'
      ),
    [pdfMeta]
  );

  const hasInvalidPageRange = useMemo(
    () => Object.values(pdfMeta).some((meta) => meta.validationError != null),
    [pdfMeta]
  );

  const hasAnyPageRange = useMemo(
    () =>
      Object.values(pdfMeta).some(
        (meta) => meta.confirmedRange != null || hasPageRangeDigits(meta.draftRange)
      ),
    [pdfMeta]
  );

  const isUploadAndScanDisabled =
    isUploadButtonDisable ||
    isUnsupportedFileType ||
    isUploadLoading ||
    hasPdfInspectError ||
    hasInvalidPageRange ||
    (isPDFOnly && !isAllFilePdf);

  const isUploadDisabled =
    isUploadButtonDisable ||
    isUploadAndScanLoading ||
    hasPdfInspectError ||
    (isOcrCompany && hasAnyPageRange);

  const uploadPopUpMessage = useMemo(() => {
    if (hasPdfInspectError) {
      return t('scanByPage.pdfInspectError');
    }
    if (isOcrCompany && hasAnyPageRange) {
      return t('scanByPage.removePageRangeToUpload');
    }
    return t('uploadFiles.selectFileToUpload');
  }, [hasAnyPageRange, hasPdfInspectError, isOcrCompany, t]);

  const { scanFilePopUpMessage } = useMemo(() => {
    let scanFileMessage = '';
    if (isPDFOnly && !isAllFilePdf) {
      scanFileMessage = 'OCR supports only .PDF formats.';
    } else if (isUnsupportedFileType) {
      scanFileMessage =
        'OCR supports only .PDF, .JPG, .JPEG and .PNG formats. If format is supported, check if file extension was renamed.';
    } else if (hasPdfInspectError) {
      scanFileMessage = t('scanByPage.pdfInspectError');
    } else if (hasInvalidPageRange) {
      scanFileMessage = t('scanByPage.fixInvalidPageRange');
    } else if (isUploadButtonDisable) {
      scanFileMessage = 'Please select a file to Upload and Scan.';
    }
    return { isUnsupportedFileType, scanFilePopUpMessage: scanFileMessage };
  }, [
    isPDFOnly,
    isUnsupportedFileType,
    isUploadButtonDisable,
    isAllFilePdf,
    hasPdfInspectError,
    hasInvalidPageRange,
    t,
  ]);

  return (
    <div>
      <Modal
        title={'Upload Files'}
        closeModal={closeModal}
        visible={open}
        width={modalWidth}
        height={modalHeight}
        className="upload-files-modal"
      >
        <div
          ref={modalContentRef}
          tabIndex={-1}
          className={`upload-files-modal__grid min-h-0 flex-1 gap-4 ${
            isWideLayout
              ? 'grid grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] items-stretch overflow-hidden'
              : 'upload-files-modal__stacked flex flex-col overflow-y-auto'
          }`}
        >
          <div
            className={`upload-files-modal__left flex flex-col ${
              isWideLayout ? 'h-full min-h-0 overflow-hidden' : 'flex-shrink-0'
            }`}
          >
            <div className="upload-files-modal__instructions flex-shrink-0 [&>p]:mb-1 [&>p]:text-sm">
              {isOcrCompany && (
                <p className="!mb-2 font-semibold">
                  Average scan time 5 minutes or less for small batches. Please allow up to 4 hours
                  for processing.
                </p>
              )}
              <p>Please select or drop the image files you want to upload.</p>
              {!isOcrCompany ? (
                <p>
                  Alternatively, attachments can be loaded by email instead by sending them to{' '}
                  <span
                    className="link text-info cursor-pointer font-semibold whitespace-nowrap"
                    onClick={handleMailClick}
                  >
                    {userEmail}
                  </span>
                  . The files will be available in the Records queue after the email has been
                  processed.
                </p>
              ) : (
                <>
                  <p>
                    If you prefer you can send an email with attachments of all the files you would
                    like to upload. Those files will be available in the records queue once
                    uploaded.
                  </p>
                  <p>
                    Then select if you would like to scan and have a document created based on the
                    image. You will find the successful scans and documents listed here in the
                    Records tab. Any issues when creating the document will be flagged as "Needs
                    Review" or "Failed" in the OCR Queue tab.
                  </p>
                  <p>
                    You can also direct your suppliers to email documents to{' '}
                    <span
                      className="link text-info cursor-pointer font-semibold whitespace-nowrap"
                      onClick={handleMailClick}
                    >
                      {userEmail}
                    </span>
                  </p>
                </>
              )}
            </div>
            {showDesktopDropZone && (
              <div
                onClick={onSelectFilesClick}
                className="invoice-external-dropzone mb-[1px] flex-shrink-0"
                onKeyDown={(e) => e.key === 'Enter' && onSelectFilesClick()}
              >
                <ExternalDropZone
                  uploadRef={uploadRef}
                  customHint={hint}
                  disabled={isUploadLoading || isUploadAndScanLoading}
                />
              </div>
            )}

            <div
              className={`upload-files-modal__file-list ${
                isWideLayout ? 'min-h-0 flex-1 overflow-hidden' : ''
              }`}
            >
              <Upload
                id="uploadInvoiceModal"
                ref={uploadRef}
                disabled={isUploadLoading || isUploadAndScanLoading}
                accept={isPDFOnly ? '.pdf' : '.pdf, image/jpg, image/jpeg, .gif, .bmp, image/png'}
                batch={false}
                multiple={true}
                defaultFiles={[]}
                withCredentials
                className="k-dropzone-hint"
                onAdd={onAdd}
                onRemove={handleRemoveFile}
                autoUpload={false}
                showActionButtons={false}
                listItemUI={renderUploadFileListItem}
              />
            </div>
          </div>
          <div
            className={
              isWideLayout
                ? 'min-h-0 h-full overflow-hidden'
                : 'upload-files-modal__preview flex-shrink-0'
            }
          >
            <UploadFilePreviewPanel
              selectedFile={selectedListFile}
              previewableFiles={previewableFiles}
              selectedPreviewIndex={selectedPreviewIndex}
              onSelectPreviewIndex={handleSelectPreviewIndex}
              previewHeight="100%"
              isOcrCompany={isOcrCompany}
              useExternalScroll={!isWideLayout}
              pdfInspectError={
                selectedListFile ? pdfMeta[selectedListFile.uid]?.error ?? null : null
              }
            />
          </div>
        </div>
        <DialogActionsBar>
          <div className="mt-2 flex w-full flex-wrap items-center justify-end gap-2">
            {isOcrCompany && (
              <PopUp
                popupAlign={{ horizontal: 'center', vertical: 'bottom' }}
                anchorAlign={{ horizontal: 'center', vertical: 'top' }}
                content={
                  <div className="m-1 max-w-[300px]">
                    <PopUpContentWrapper type="Information" contents={[scanFilePopUpMessage]} />
                  </div>
                }
                show={isUploadAndScanDisabled}
              >
                <Button
                  color="info"
                  onClick={() => handleUploadAndScan(true)}
                  disabled={isUploadAndScanDisabled}
                  loading={isUploadAndScanLoading}
                >
                  Upload and Scan File(s)
                </Button>
              </PopUp>
            )}
            <PopUp
              popupAlign={{ horizontal: 'center', vertical: 'bottom' }}
              anchorAlign={{ horizontal: 'center', vertical: 'top' }}
              content={
                <div className="m-1 max-w-[300px]">
                  <PopUpContentWrapper type="Information" contents={[uploadPopUpMessage]} />
                </div>
              }
              show={isUploadDisabled}
            >
              <Button
                color="info"
                disabled={isUploadDisabled}
                onClick={() => handleUploadAndScan(false)}
                loading={isUploadLoading}
              >
                Upload
              </Button>
            </PopUp>
            <Button
              color="info"
              onClick={closeModal}
              disabled={isUploadLoading || isUploadAndScanLoading}
            >
              Cancel
            </Button>
          </div>
        </DialogActionsBar>
      </Modal>
    </div>
  );
};

export default InvoiceUploadFilesModal;
